import { StorageService } from './Storage.ts';

const TELEMETRY_KEY = 'vip_tray_telemetry_v1';
const SESSION_FLAG_KEY = 'vip_tray_session_active';

export type PlayerCohort = '0_games' | '1_game' | '2_to_5_games' | '6_plus_games';
export type PlayedMultiple = 'none' | 'no' | 'yes';

export interface TelemetryData {
  totalGamesPlayed: number;
  sessionsCount: number;
  firstGameMilestoneSent: boolean;
  repeatGameMilestoneSent: boolean;
}

export function defaultTelemetryData(): TelemetryData {
  return {
    totalGamesPlayed: 0,
    sessionsCount: 0,
    firstGameMilestoneSent: false,
    repeatGameMilestoneSent: false,
  };
}

export function loadTelemetryData(): TelemetryData {
  const def = defaultTelemetryData();
  try {
    const raw = localStorage.getItem(TELEMETRY_KEY);
    if (!raw) return def;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return def;
    return {
      totalGamesPlayed: typeof parsed.totalGamesPlayed === 'number' && parsed.totalGamesPlayed >= 0 ? parsed.totalGamesPlayed : def.totalGamesPlayed,
      sessionsCount: typeof parsed.sessionsCount === 'number' && parsed.sessionsCount >= 0 ? parsed.sessionsCount : def.sessionsCount,
      firstGameMilestoneSent: Boolean(parsed.firstGameMilestoneSent),
      repeatGameMilestoneSent: Boolean(parsed.repeatGameMilestoneSent),
    };
  } catch {
    return def;
  }
}

export function saveTelemetryData(data: TelemetryData): void {
  try {
    localStorage.setItem(TELEMETRY_KEY, JSON.stringify(data));
  } catch {
    // Ignore Safari private browsing or storage quota errors
  }
}

/** Чистая функция: определяет когорту игрока по общему числу завершённых партий. */
export function getPlayerCohort(totalGames: number): PlayerCohort {
  if (totalGames <= 0) return '0_games';
  if (totalGames === 1) return '1_game';
  if (totalGames <= 5) return '2_to_5_games';
  return '6_plus_games';
}

/** Чистая функция: флаг "сыграл > 1 раза" для быстрых фильтров и сегментов в GA4. */
export function getPlayedMultipleFlag(totalGames: number): PlayedMultiple {
  if (totalGames <= 0) return 'none';
  if (totalGames === 1) return 'no';
  return 'yes';
}

/** Чистая функция: проверяет наличие флага "Do Not Track" в браузере. */
export function isDoNotTrackEnabled(
  nav?: { doNotTrack?: string | null },
  win?: unknown,
): boolean {
  const n = nav?.doNotTrack;
  const w = win && typeof win === 'object' && 'doNotTrack' in win
    ? (win as { doNotTrack?: string | null }).doNotTrack
    : undefined;
  return n === '1' || n === 'yes' || w === '1';
}

export function getMeasurementId(): string | undefined {
  try {
    return import.meta.env.VITE_GA_MEASUREMENT_ID;
  } catch {
    return undefined;
  }
}

let isScriptInjected = false;
let isTelemetryLoaded = false;
let currentTelemetry: TelemetryData = defaultTelemetryData();

function ensureTelemetryLoaded(): TelemetryData {
  if (!isTelemetryLoaded) {
    currentTelemetry = loadTelemetryData();
    isTelemetryLoaded = true;
  }
  return currentTelemetry;
}

export function getTelemetryState(): TelemetryData {
  return ensureTelemetryLoaded();
}

/** Проверяет, включена ли аналитика с учётом настройки пользователя и флага DNT. */
export function isAnalyticsActiveInUI(): boolean {
  if (!StorageService.isAnalyticsEnabled()) return false;
  if (typeof navigator !== 'undefined' && typeof window !== 'undefined') {
    if (isDoNotTrackEnabled(navigator, window)) return false;
  }
  return true;
}

/** Проверяет, активен ли сбор аналитики прямо сейчас (есть ID + согласие + нет DNT). */
export function canTrack(): boolean {
  const id = getMeasurementId();
  if (!id) return false;
  return isAnalyticsActiveInUI();
}

/** Вспомогательный вызов gtag с безопасной проверкой доступности и соблюдением формата IArguments. */
function sendGtag(...args: unknown[]): void {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
  } else if (Array.isArray(window.dataLayer)) {
    // gtag.js ожидает объект IArguments в очереди dataLayer
    (function (..._a: unknown[]) {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    })(...args);
  }
}

/** Загружает и инициализирует Google Analytics 4, если задан ID и получено согласие. */
export function initAnalytics(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  ensureTelemetryLoaded();

  // Регистрация новой сессии независимо от наличия ключа GA4, чтобы счётчик сессий был точным
  try {
    if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(SESSION_FLAG_KEY)) {
      sessionStorage.setItem(SESSION_FLAG_KEY, '1');
      currentTelemetry.sessionsCount += 1;
      saveTelemetryData(currentTelemetry);
    }
  } catch {
    // sessionStorage недоступен (например, в Safari Private Mode)
  }

  const measurementId = getMeasurementId();
  if (!measurementId) return;

  // Проверка согласия и DNT
  if (!isAnalyticsActiveInUI()) {
    window[`ga-disable-${measurementId}`] = true;
    return;
  }

  window[`ga-disable-${measurementId}`] = false;

  if (!isScriptInjected) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // Стандартная спецификация gtag.js требует передачи объекта arguments в dataLayer
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
    sendGtag('js', new Date());
    sendGtag('config', measurementId, {
      send_page_view: false,
      anonymize_ip: true,
    });

    // Динамическая асинхронная вставка скрипта gtag.js
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.onerror = () => {
      // Игнорируем сетевые ошибки при работе в изолированном офлайн-режиме
    };
    document.head.appendChild(script);

    isScriptInjected = true;
  }

  syncUserProperties();
}

/** Управляет включением/отключением сбора при изменении настроек в рантайме. */
export function setAnalyticsConsent(enabled: boolean): void {
  StorageService.setAnalyticsEnabled(enabled);

  const measurementId = getMeasurementId();
  if (!measurementId || typeof window === 'undefined') return;

  if (!enabled || isDoNotTrackEnabled(navigator, window)) {
    window[`ga-disable-${measurementId}`] = true;
  } else {
    window[`ga-disable-${measurementId}`] = false;
    if (!isScriptInjected) {
      initAnalytics();
    } else {
      syncUserProperties();
    }
  }
}

/** Синхронизирует свойства пользователя (когорта, число партий, рекорд, язык, модель, PWA-режим). */
export function syncUserProperties(): void {
  if (!canTrack()) return;

  ensureTelemetryLoaded();
  const total = currentTelemetry.totalGamesPlayed;
  const cohort = getPlayerCohort(total);
  const playedMultiple = getPlayedMultipleFlag(total);

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true);

  sendGtag('set', 'user_properties', {
    player_cohort: cohort,
    played_multiple: playedMultiple,
    total_games_played: total,
    sessions_count: currentTelemetry.sessionsCount,
    high_score: StorageService.getHighScore(),
    preferred_lang: StorageService.getLanguage(),
    selected_girl: StorageService.getSelectedGirl(),
    display_mode: isStandalone ? 'standalone' : 'browser',
  });
}

/** Отслеживает просмотр экрана/оверлея (menu, playing, pause, gameover, rules). */
export function trackScreenView(screenName: string): void {
  if (!canTrack()) return;
  sendGtag('event', 'screen_view', {
    screen_name: screenName,
  });
}

/** Отслеживает начало партии. */
export function trackGameStart(params: { selectedGirl: string; highScore: number }): void {
  if (!canTrack()) return;
  sendGtag('event', 'game_start', {
    selected_girl: params.selectedGirl,
    high_score: params.highScore,
  });
}

/** Отслеживает повышение уровня (Стрип-Клуб -> Пентхаус -> Ибица). */
export function trackLevelUp(params: { levelId: number; levelTheme: string; score: number }): void {
  if (!canTrack()) return;
  sendGtag('event', 'level_up', {
    level_id: params.levelId,
    level_theme: params.levelTheme,
    score: params.score,
  });
}

/** Отслеживает завершение партии и ключевые когортные вехи (1-я партия, >1 партий). */
export function trackGameEnd(params: {
  score: number;
  highScore: number;
  isNewRecord: boolean;
  levelReached: number;
  levelTheme: string;
  selectedGirl: string;
}): void {
  ensureTelemetryLoaded();
  currentTelemetry.totalGamesPlayed += 1;
  saveTelemetryData(currentTelemetry);

  const totalGames = currentTelemetry.totalGamesPlayed;
  const cohort = getPlayerCohort(totalGames);
  const playedMultiple = getPlayedMultipleFlag(totalGames);

  if (!canTrack()) return;

  // 1. Стандартное событие окончания игры
  sendGtag('event', 'game_complete', {
    score: params.score,
    high_score: params.highScore,
    is_new_record: params.isNewRecord,
    level_reached: params.levelReached,
    level_theme: params.levelTheme,
    selected_girl: params.selectedGirl,
    games_played_total: totalGames,
    player_cohort: cohort,
    played_multiple: playedMultiple,
  });

  // 2. Веховое событие: первая завершённая партия (сыграл хотя бы 1 раз)
  if (totalGames >= 1 && !currentTelemetry.firstGameMilestoneSent) {
    currentTelemetry.firstGameMilestoneSent = true;
    saveTelemetryData(currentTelemetry);
    sendGtag('event', 'first_game_complete', {
      score: params.score,
      level_reached: params.levelReached,
      selected_girl: params.selectedGirl,
    });
  }

  // 3. Веховое событие: повторная партия (сыграл БОЛЬШЕ 1 раза)
  if (totalGames >= 2 && !currentTelemetry.repeatGameMilestoneSent) {
    currentTelemetry.repeatGameMilestoneSent = true;
    saveTelemetryData(currentTelemetry);
    sendGtag('event', 'repeat_game_complete', {
      score: params.score,
      level_reached: params.levelReached,
      games_played_total: totalGames,
    });
  }

  // Обновляем пользовательские свойства в GA4
  syncUserProperties();
}

/** Отслеживает выбор модели девушки на подносе. */
export function trackGirlSelect(modelId: string): void {
  if (!canTrack()) return;
  sendGtag('event', 'girl_model_select', {
    model_id: modelId,
  });
  syncUserProperties();
}

/** Отслеживает изменение настроек (язык, звук, аналитика). */
export function trackSettingsChange(settingName: string, value: string | boolean | number): void {
  if (!canTrack()) return;
  sendGtag('event', 'settings_change', {
    setting_name: settingName,
    setting_value: String(value),
  });
  syncUserProperties();
}

/** Отслеживает установку PWA на устройство. */
export function trackPwaInstalled(): void {
  if (!canTrack()) return;
  sendGtag('event', 'pwa_installed');
}
